import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    IconButton,
    Divider,
    Tooltip,
    Select,
    Option,
    Button,
    Stack,
} from "@mui/joy";
import { Minus, Plus, Trash2, CircleAlert, RefreshCcw, X, PlusCircle } from "lucide-react";
import { motion } from "framer-motion";
import type { MaterialLine } from "./hooks/utils/materialLineHelpers";
import { getMultiLineTotalQuantity, getSizeAccentColor } from "./hooks/utils/materialLineHelpers";

interface MultiLineProductCardProps {
    categoryName: string;
    units?: string;
    productOptions: string[];
    lines: MaterialLine[];
    getPriceDisplay: (line: MaterialLine) => { display: string; label: string };
    prerequisites?: { name: string; isSatisfied: boolean }[];
    onLineProductChange: (lineKey: string, product: string) => void;
    onLineQuantityUpdate: (lineKey: string, direction: "add" | "remove") => void;
    onAddLine: () => void;
    onRemoveLine: (lineKey: string) => void;
    onClearAll: () => void;
    onReevaluate?: () => void;
}

const lineRowGridSx = {
    display: "grid",
    gridTemplateColumns: {
        xs: "1fr",
        sm: "minmax(140px, 1fr) auto 140px auto",
    },
    alignItems: "center",
    columnGap: 1.5,
    rowGap: 1,
    width: "100%",
} as const;

function QuantityControl({
    quantity,
    onAdd,
    onRemove,
}: {
    quantity: number;
    onAdd: () => void;
    onRemove: () => void;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                borderRadius: "40px",
                p: 0.2,
                pb: 0.15,
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: "divider",
                width: "fit-content",
                flexShrink: 0,
            }}
        >
            <IconButton
                variant="soft"
                size="sm"
                color="danger"
                onClick={onRemove}
                sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}
            >
                <Minus size={12} />
            </IconButton>
            <Typography level="body-sm" sx={{ fontWeight: "md", minWidth: 24, textAlign: "center" }} component="span">
                {quantity}
            </Typography>
            <IconButton
                variant="soft"
                color="success"
                size="sm"
                onClick={onAdd}
                sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}
            >
                <Plus size={12} />
            </IconButton>
        </Box>
    );
}

function MaterialLineRow({
    line,
    productOptions,
    getPriceDisplay,
    onLineProductChange,
    onLineQuantityUpdate,
    onRemoveLine,
}: {
    line: MaterialLine;
    productOptions: string[];
    getPriceDisplay: (line: MaterialLine) => { display: string; label: string };
    onLineProductChange: (lineKey: string, product: string) => void;
    onLineQuantityUpdate: (lineKey: string, direction: "add" | "remove") => void;
    onRemoveLine: (lineKey: string) => void;
}) {
    const { display: priceDisplay, label: priceLabel } = getPriceDisplay(line);
    const selectedProduct = productOptions.includes(line.value) ? line.value : null;
    const accentColor = getSizeAccentColor(line.value || "");

    return (
        <Box
            sx={{
                ...lineRowGridSx,
                pl: 1.5,
                pr: 1,
                py: 0.75,
                borderRadius: "sm",
                bgcolor: "background.level1",
                border: "1px solid",
                borderColor: "divider",
                borderLeft: "3px solid",
                borderLeftColor: accentColor,
            }}
        >
            <Select
                size="sm"
                value={selectedProduct || ""}
                placeholder="Size"
                onChange={(_e, val) => val && onLineProductChange(line.lineKey, val as string)}
                slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                sx={{ width: "100%", maxWidth: { sm: 220 } }}
            >
                {productOptions.map((opt) => (
                    <Option key={opt} value={opt}>
                        {opt}
                    </Option>
                ))}
            </Select>

            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "center" } }}>
                <QuantityControl
                    quantity={line.quantity || 0}
                    onAdd={() => onLineQuantityUpdate(line.lineKey, "add")}
                    onRemove={() => onLineQuantityUpdate(line.lineKey, "remove")}
                />
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography level="body-sm" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {priceDisplay}
                </Typography>
                <Typography level="body-xs">{priceLabel}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                    size="sm"
                    variant="soft"
                    color="danger"
                    onClick={() => onRemoveLine(line.lineKey)}
                    title="Remove size"
                    sx={{ "--IconButton-size": "28px", border: "1px solid", borderColor: "divider", borderRadius: 6 }}
                >
                    <X size={14} />
                </IconButton>
            </Box>
        </Box>
    );
}

export function MultiLineProductCard({
    categoryName,
    units,
    productOptions,
    lines,
    getPriceDisplay,
    prerequisites,
    onLineProductChange,
    onLineQuantityUpdate,
    onAddLine,
    onRemoveLine,
    onClearAll,
    onReevaluate,
}: MultiLineProductCardProps) {
    const hasIssue = lines.length === 0 || lines.some((line) => !line.value || !line.quantity);
    const totalQty = getMultiLineTotalQuantity(lines);

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: "divider",
                zIndex: 2,
                width: "100%",
            }}
            variant="soft"
        >
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1} minWidth={0}>
                        <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                            {categoryName}
                        </Typography>
                        {units && <Typography level="body-xs">({units})</Typography>}
                        {lines.length > 0 && (
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                · {lines.length} line{lines.length !== 1 ? "s" : ""}, {totalQty} qty
                            </Typography>
                        )}

                        {hasIssue && (
                            <Tooltip title="Add at least one size with quantity" arrow variant="solid" color="warning">
                                <motion.div
                                    animate={{ opacity: [1, 0.5, 1], scale: [1, 1.1, 1] }}
                                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                    style={{ display: "flex", alignItems: "center" }}
                                >
                                    <CircleAlert size={18} color="#ed6c02" />
                                </motion.div>
                            </Tooltip>
                        )}

                        {prerequisites?.map((prereq) => (
                            <Tooltip
                                key={prereq.name}
                                title={prereq.isSatisfied ? `Requires ${prereq.name}` : `Select ${prereq.name} first`}
                                arrow
                                variant="soft"
                            >
                                <Chip
                                    size="sm"
                                    sx={{ borderRadius: 6, border: "1px solid", borderColor: "divider" }}
                                    color={prereq.isSatisfied ? "success" : "danger"}
                                >
                                    {prereq.name}
                                </Chip>
                            </Tooltip>
                        ))}
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                        {onReevaluate && (
                            <IconButton
                                size="sm"
                                color="primary"
                                variant="soft"
                                onClick={onReevaluate}
                                title="Sync from inspection"
                                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6, "--IconButton-size": "26px" }}
                            >
                                <RefreshCcw size={12} />
                            </IconButton>
                        )}
                        <IconButton
                            size="sm"
                            color="danger"
                            variant="soft"
                            onClick={onClearAll}
                            title="Clear all"
                            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6, "--IconButton-size": "26px" }}
                        >
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>

                <Stack spacing={0.75} sx={{ width: "100%" }}>
                    {lines.length === 0 ? (
                        <Typography level="body-xs" sx={{ color: "text.tertiary", py: 0.5 }}>
                            No sizes added
                        </Typography>
                    ) : (
                        lines.map((line) => (
                            <MaterialLineRow
                                key={line.lineKey}
                                line={line}
                                productOptions={productOptions}
                                getPriceDisplay={getPriceDisplay}
                                onLineProductChange={onLineProductChange}
                                onLineQuantityUpdate={onLineQuantityUpdate}
                                onRemoveLine={onRemoveLine}
                            />
                        ))
                    )}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: "inline-flex" }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<PlusCircle size={14} />}
                        onClick={onAddLine}
                        disabled={productOptions.length === 0}
                    >
                        Add size
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}

export default MultiLineProductCard;
